/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/DynamicDestinationSections/OptionsSection.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Options section component for Dynamic Destination form.
────────────────────────────────────────────────────────────────*/

import { Plus, Trash2, Share2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import Toggle from '../../../../components/ui/Toggle';
import { FormBox } from '../../../../components/ui/FormBox';
import ModuleDestinationSelect from '../../../../components/ui/ModuleDestinationSelect';
import { FormState, DynamicDestinationOptionFormData, OptionErrors } from '../../types';
import { useI18n } from '../../../../hooks/useI18n';

interface OptionsSectionProps {
  formData: FormState;
  optionErrors: OptionErrors[];
  onOptionChange: <K extends keyof DynamicDestinationOptionFormData>(
    index: number,
    field: K,
    value: DynamicDestinationOptionFormData[K]
  ) => void;
  onOptionModuleChange: (index: number, moduleValue: string) => void;
  onAddOption: () => void;
  onRemoveOption: (index: number) => void;
}

export function OptionsSection({
  formData,
  optionErrors,
  onOptionChange,
  onOptionModuleChange,
  onAddOption,
  onRemoveOption
}: OptionsSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox
      title={t('dynamicDestination.sections.options')}
      icon={<Share2 className="h-5 w-5" />}
      description={t('dynamicDestination.sections.optionsDescription')}
    >
      <div className="space-y-4">
        {formData.options.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t('dynamicDestination.options.empty')}
          </div>
        ) : (
          <>
            <div className="hidden grid-cols-12 gap-3 px-4 text-xs font-semibold uppercase text-muted-foreground md:grid">
              <div className="col-span-3">{t('dynamicDestination.fields.matchValue.label')}</div>
              <div className="col-span-5">{t('dynamicDestination.fields.destination.label')}</div>
              <div className="col-span-2">{t('common.enabled')}</div>
              <div className="col-span-2 text-right">{t('common.actions')}</div>
            </div>
            <div className="rounded-md border border-border divide-y divide-border">
              {formData.options.map((option, index) => (
                <div
                  key={option.id ?? index}
                  className="grid grid-cols-1 gap-4 px-4 py-3 md:grid-cols-12 md:items-center"
                >
                  <div className="md:col-span-3 space-y-2">
                    <div className="md:hidden text-xs font-semibold uppercase text-muted-foreground">
                      {t('dynamicDestination.fields.matchValue.label')}
                    </div>
                    <Input
                      id={`matchValue-${index}`}
                      value={option.matchValue}
                      onChange={(event) =>
                        onOptionChange(index, 'matchValue', event.target.value)
                      }
                      placeholder={t('dynamicDestination.fields.matchValue.placeholder')}
                      error={optionErrors[index]?.matchValue}
                    />
                  </div>
                  <div className="md:col-span-5 space-y-2">
                    <div className="md:hidden text-xs font-semibold uppercase text-muted-foreground">
                      {t('dynamicDestination.fields.destination.label')}
                    </div>
                    <ModuleDestinationSelect
                      moduleValue={option.destinationModule || ''}
                      destinationValue={option.destinationValue || ''}
                      onModuleChange={(value) => onOptionModuleChange(index, value || '')}
                      onDestinationChange={(value) =>
                        onOptionChange(index, 'destinationValue', value || '')
                      }
                      modulePlaceholder={t('dynamicDestination.fields.destinationModule.placeholder')}
                      destinationPlaceholder={t('dynamicDestination.fields.destination.placeholder')}
                    />
                    {optionErrors[index]?.destinationModule ? (
                      <p className="mt-1 text-xs text-destructive">
                        {optionErrors[index]?.destinationModule}
                      </p>
                    ) : null}
                    {optionErrors[index]?.destinationValue ? (
                      <p className="mt-1 text-xs text-destructive">
                        {optionErrors[index]?.destinationValue}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <div className="md:hidden text-xs font-semibold uppercase text-muted-foreground">
                      {t('common.enabled')}
                    </div>
                    <Toggle
                      id={`enabled-${index}`}
                      value={option.enabled}
                      onChange={(value) => onOptionChange(index, 'enabled', value)}
                    />
                  </div>
                  <div className="flex items-start justify-end md:col-span-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveOption(index)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">{t('dynamicDestination.options.remove')}</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onAddOption} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('common.add')}
          </Button>
        </div>
      </div>
    </FormBox>
  );
}

